import { NsCard } from 'components/common/ns-card/NsCard';
import { NsPrice } from 'components/common/ns-price/NsPrice';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsContent } from 'components/common/ns-content/NsContent';
import { Error } from 'components/common/error/Error';
import { NsDescriptionList } from 'components/common/ns-description-list/NsDescriptionList';
import NsInputterEvent from 'components/common/ns-inputter/NsInputterEvent';

import { useOrderSummary } from 'components/direct-sales/order-summary/hooks/useOrderSummary';
import { useFmrsPayload } from 'components/direct-sales/order-summary/hooks/useFmrsPayload';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useNavigation } from 'hooks/useNavigation';

import { sendSalesOrderThunk } from 'state/sales-order/sales-order-thunks';
import { TermsAndConditions } from './TermsAndConditions';
import { Agreement } from './Agreement';
import { NsForm } from 'components/common/ns-form/NsForm';
import { formatTariffDate } from 'components/direct-sales/quote/utils';

export const OrderSummaryTariffDetails = () => {
  const dispatch = useAppDispatch();
  const { customerPreferredStartDate } = useFmrsPayload();

  const {
    setTermsValue,
    salesOrderPayload,
    errors,
    isLoading,
    tariffTermsPDF,
    isTOU,
    productName,
    monthlyAmount,
    electricityQuote,
    gasQuote,
    annualAmount,
    exitFee,
    tariffEndsOn
  } = useOrderSummary();

  const { eventTypes, next } = useNavigation();

  const handleChange = (event: NsInputterEvent) => {
    const target = event.target as JSX.IntrinsicElements['ns-inputter'];
    const value = target.value;
    setTermsValue(!!value);
  };
  const handleGoNext = async () => {
    try {
      await dispatch(sendSalesOrderThunk(salesOrderPayload));
      next({ eventType: eventTypes.NEXT });
    } catch (e) {
      e && console.error(e);
    }
  };
  return (
    <NsForm onSubmit={handleGoNext}>
      <NsContent spacing="normal">
        <h2 className="h3">Order summary</h2>
      </NsContent>

      <NsCard>
        <NsDescriptionList>
          {[
            {
              term: 'Your tariff',
              description: [
                <>{productName}</>,
                <>
                  <strong>Estimated cost</strong>
                </>,
                <>
                  <NsPrice amount={monthlyAmount} /> /month (inc. VAT)
                </>
              ]
            },
            {
              term: 'Estimated annual usage',
              description: [
                <>
                  {electricityQuote && (
                    <>
                      <p>
                        <strong>Electricity</strong>
                      </p>
                      <p>
                        {electricityQuote.providedAnnualConsumption
                          ? electricityQuote.providedAnnualConsumption
                          : (electricityQuote.providedAnnualConsumptionDay ??
                              0) +
                            (electricityQuote.providedAnnualConsumptionNight ??
                              0)}{' '}
                        kWh
                      </p>
                      <p>
                        <NsPrice amount={electricityQuote.annualAmount} /> (inc.
                        VAT)
                      </p>
                      <br />
                    </>
                  )}
                  {gasQuote && (
                    <>
                      <p>
                        <strong>Gas</strong>
                      </p>
                      <p>{gasQuote.providedAnnualConsumption} kWh</p>
                      <p>
                        <NsPrice amount={gasQuote.annualAmount} /> (inc. VAT)
                      </p>
                      <br />
                    </>
                  )}
                  <p>
                    <strong>Total</strong>
                  </p>
                  <p>
                    <NsPrice amount={annualAmount} /> (inc. VAT)
                  </p>
                </>
              ]
            },
            electricityQuote && {
              term: 'Electricity',
              description: (
                <>
                  <p>
                    <strong>Standing charge</strong>
                  </p>
                  <p>{electricityQuote.standingCharge}p per day (inc. VAT)</p>
                  <br />
                  {electricityQuote.unitRate ? (
                    <>
                      <p>
                        <strong>Unit rate</strong>
                      </p>
                      <p>{electricityQuote.unitRate}p per kWh (inc. VAT)</p>
                      <br />
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>{electricityQuote.unitRateDayName}</strong>
                      </p>
                      <p>{electricityQuote.unitRateDay}p per kWh (inc. VAT)</p>
                      <p>{electricityQuote.unitRateNightName}</p>
                      <p>
                        {electricityQuote.unitRateNight}p per kWh (inc. VAT)
                      </p>
                      <br />
                    </>
                  )}
                  <p>
                    <strong>Exit fee</strong>
                  </p>
                  <p>{exitFee}</p>
                </>
              )
            },
            gasQuote && {
              term: 'Gas',
              description: (
                <>
                  <p>
                    <strong>Standing charge</strong>
                  </p>
                  <p>{gasQuote.standingCharge}p per day (inc. VAT)</p>
                  <br />
                  <p>
                    <strong>Unit rate</strong>
                  </p>
                  <p>{gasQuote.unitRate}p per kWh (inc. VAT)</p>
                  <br />
                  <p>
                    <strong>Exit fee</strong>
                  </p>
                  <p>{exitFee}</p>
                </>
              )
            },
            {
              term: 'Supply start date',
              description: formatTariffDate(customerPreferredStartDate)
            },
            {
              term: 'Tariff ends on',
              description: tariffEndsOn
                ? formatTariffDate(tariffEndsOn)
                : 'Not Applicable'
            },
            {
              term: 'Supplier',
              description: 'British Gas Trading Limited'
            }
          ]}
        </NsDescriptionList>
        <br />
        <NsContent>
          <Agreement isTOU={isTOU} />
          <TermsAndConditions
            handleChange={handleChange}
            tariffTermsPDF={tariffTermsPDF || undefined}
            isTOU={isTOU}
          />
          <Error errors={errors} />
          <NsPrimaryCta isLoading={isLoading}>
            Complete your switch
          </NsPrimaryCta>
        </NsContent>
      </NsCard>
    </NsForm>
  );
};

export default OrderSummaryTariffDetails;
